
import React, { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OverviewCards from "@/components/admin/OverviewCards";
import UsersTable from "@/components/admin/UsersTable";
import ApiControls from "@/components/admin/ApiControls";
import ApiKeySync from "@/components/admin/ApiKeySync";
import SuggestionsTable from "@/components/admin/SuggestionsTable";
import UpdatesForm from "@/components/admin/UpdatesForm";
import NotificationsTab from "@/components/admin/NotificationsTab";
import VideosTab from "@/components/admin/VideosTab";
import ToursTab from "@/components/admin/ToursTab";
import { AdminRolesManager } from "@/components/admin/AdminRolesManager";
import { Shield } from "lucide-react";
import { useAdminPanel } from "@/hooks/useAdminPanel";
import { useAdmin } from "@/context/AdminContext";
import { formatDate, formatStorageLimit } from "@/utils/formatters";
import { IndicesTab } from "@/components/admin/IndicesTab";
import { StatisticsTab } from "@/components/admin/StatisticsTab";
import AnnouncementsTab from "@/components/admin/AnnouncementsTab";
import CouponsTab from "@/components/admin/CouponsTab";
import JurisprudenciaTab from "@/components/admin/JurisprudenciaTab";


const AdminPanel = () => {
  const { setIsAdminPage } = useAdmin();
  
  const {
    isLoading,
    users,
    filteredUsers,
    subscriptions,
    isRefreshing,
    isSyncing,
    isUpdatingPlan,
    searchQuery,
    setSearchQuery,
    connectionFilter,
    setConnectionFilter,
    planFilter,
    setPlanFilter,
    chatAccessFilter,
    setChatAccessFilter,
    handleRefresh,
    handleSyncUsers,
    handleUpdateStorageLimit,
    handleToggleApproval,
    getUserSessionInfo,
  } = useAdminPanel();

  // Notificar o AdminContext que estamos na página admin
  useEffect(() => {
    console.log("AdminPanel: Ativando modo administrativo");
    setIsAdminPage(true);
    
    return () => {
      console.log("AdminPanel: Desativando modo administrativo");
      setIsAdminPage(false);
    };
  }, [setIsAdminPage]);

  const getSubscriptionForUser = (userId: string) => {
    return subscriptions.find(sub => sub.user_id === userId);
  };

  const handleDeleteUser = async (userId: string) => {
    console.log("Delete user:", userId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-8 w-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Carregando painel administrativo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-[calc(100vw-3rem)] mx-6 py-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Painel Administrativo</h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Gerencie usuários, monitore o sistema e configure as APIs do Chat Jurídico
          </p>
        </div>

        {/* Overview Cards */}
        <OverviewCards 
          users={users}
          subscriptions={subscriptions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
          onSync={handleSyncUsers}
          isRefreshing={isRefreshing}
          isSyncing={isSyncing}
          connectionFilter={connectionFilter}
          planFilter={planFilter}
          chatAccessFilter={chatAccessFilter}
          onConnectionFilterChange={setConnectionFilter}
          onPlanFilterChange={setPlanFilter}
          onChatAccessFilterChange={setChatAccessFilter}
          filteredCount={filteredUsers.length}
        />

        {/* Main Content */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' }}>
            <TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="statistics">Estatísticas</TabsTrigger>
            <TabsTrigger value="suggestions">Sugestões</TabsTrigger>
            <TabsTrigger value="notifications">Notificações</TabsTrigger>
            <TabsTrigger value="videos">Vídeos</TabsTrigger>
            <TabsTrigger value="updates">Atualizações</TabsTrigger>
            <TabsTrigger value="apis">APIs</TabsTrigger>
            <TabsTrigger value="indices">Índices</TabsTrigger>
            <TabsTrigger value="tours">Tours</TabsTrigger>
            <TabsTrigger value="avisos">Avisos</TabsTrigger>
            <TabsTrigger value="cupons">Cupons</TabsTrigger>
            <TabsTrigger value="jurisprudencias">Jurisprudência</TabsTrigger>
            <TabsTrigger value="admins">Admins</TabsTrigger>
            <TabsTrigger value="sync">Sincronização</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciamento de Usuários</CardTitle>
                <CardDescription>
                  Visualize e gerencie todos os usuários do sistema
                  {isUpdatingPlan && (
                    <span className="ml-2 text-sm text-blue-600">
                      • Atualizando plano...
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <UsersTable 
                  users={filteredUsers}
                  getSubscriptionForUser={getSubscriptionForUser}
                  getUserSessionInfo={getUserSessionInfo}
                  formatDate={formatDate}
                  formatStorageLimit={formatStorageLimit}
                  onUpdateStorageLimit={handleUpdateStorageLimit}
                  onToggleApproval={handleToggleApproval}
                  onDeleteUser={handleDeleteUser}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="statistics" className="space-y-6">
            <StatisticsTab />
          </TabsContent>
          
          <TabsContent value="suggestions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestão de Sugestões</CardTitle>
                <CardDescription>
                  Gerencie sugestões enviadas pelos usuários
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SuggestionsTable />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="notifications" className="space-y-6">
            <NotificationsTab />
          </TabsContent>

          <TabsContent value="videos" className="space-y-6">
            <VideosTab />
          </TabsContent>

          <TabsContent value="updates" className="space-y-6">
            <UpdatesForm />
          </TabsContent>
          
          <TabsContent value="apis" className="space-y-6">
            <ApiControls />
          </TabsContent>

          <TabsContent value="indices" className="space-y-6">
            <IndicesTab />
          </TabsContent>

          <TabsContent value="tours" className="space-y-6">
            <ToursTab />
          </TabsContent>

          <TabsContent value="avisos" className="space-y-6">
            <AnnouncementsTab />
          </TabsContent>

          <TabsContent value="cupons" className="space-y-6">
            <CouponsTab />
          </TabsContent>

          <TabsContent value="jurisprudencias" className="space-y-6">
            <JurisprudenciaTab />
          </TabsContent>

          <TabsContent value="admins" className="space-y-6">
            <AdminRolesManager />
          </TabsContent>

          <TabsContent value="sync" className="space-y-6">
            <ApiKeySync />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
